/**
 * VoucherTemplateEditor (2026-07-20 Phase 2)
 * Per-tenant editor supporting multiple voucher templates (nested schema).
 *
 * - Tabs to switch between templates: simple, classic, classic-orange, classic-blue, compact
 * - Live preview using the active template + WORKING (unsaved) settings
 * - "Simpan" persists changes for the ACTIVE template
 * - "Reset ke Default" resets only the active template
 */
import React, { useMemo, useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { RotateCcw, Save, Printer } from 'lucide-react';
import SimpleTextTemplate from '../components/templates/SimpleTextTemplate';
import ClassicTemplate from '../components/templates/ClassicTemplate';
import ClassicOrangeTemplate from '../components/templates/ClassicOrangeTemplate';
import ClassicBlueTemplate from '../components/templates/ClassicBlueTemplate';
import CompactTemplate from '../components/templates/CompactTemplate';
import QRVoucherTemplate from '../components/templates/QRVoucherTemplate';
import BatchHeader from '../components/templates/BatchHeader';
import useVoucherTemplateSettings, { VT_DEFAULTS, VT_TEMPLATE_KEYS } from '../hooks/useVoucherTemplateSettings';

const PREVIEW_VOUCHERS = Array.from({ length: 16 }, (_, i) => ({
  id: 'p' + (i + 1),
  voucher_code: 'ADZ' + String(700000 + i * 37).padStart(6, '0'),
  price: 3000,
  package_name: 'HOTSPOT 12 JAM',
}));

const TEMPLATE_LABELS = {
  'simple': 'Simple Text',
  'classic': 'Classic (Green)',
  'classic-orange': 'Classic Orange',
  'classic-blue': 'Classic Blue',
  'compact': 'Compact',
  'qr': 'QR Code',
};

const renderPreview = (tk, props) => {
  switch (tk) {
    case 'simple': return <SimpleTextTemplate {...props} />;
    case 'classic': return <ClassicTemplate {...props} />;
    case 'classic-orange': return <ClassicOrangeTemplate {...props} />;
    case 'classic-blue': return <ClassicBlueTemplate {...props} />;
    case 'compact': return <CompactTemplate {...props} />;
    case 'qr': return <QRVoucherTemplate {...props} />;
    default: return <SimpleTextTemplate {...props} />;
  }
};

const fontClamp = (v) => Math.max(4, Math.min(40, Number(v) || 0));

const VoucherTemplateEditor = () => {
  const { settings: saved, loading, saving, saveTemplate } = useVoucherTemplateSettings();
  const [activeTpl, setActiveTpl] = useState('simple');
  // draft is the WORKING copy (nested, same shape as `saved`)
  const [draft, setDraft] = useState(saved);

  useEffect(() => {
    if (saved) setDraft(saved);
  }, [saved]);

  const activeDraft = draft?.[activeTpl] || VT_DEFAULTS[activeTpl];
  const activeSaved = saved?.[activeTpl] || VT_DEFAULTS[activeTpl];

  const isDirty = useMemo(() => {
    return Object.keys(VT_DEFAULTS[activeTpl] || {}).some((k) => (activeDraft?.[k] ?? '') !== (activeSaved?.[k] ?? ''));
  }, [activeDraft, activeSaved, activeTpl]);

  const patch = (k, v) => setDraft((d) => ({
    ...d,
    [activeTpl]: { ...(d?.[activeTpl] || {}), [k]: v },
  }));

  const handleSave = async () => {
    const res = await saveTemplate(activeTpl, activeDraft);
    if (res.ok) toast.success(`Template "${TEMPLATE_LABELS[activeTpl]}" tersimpan`);
    else toast.error(`Gagal menyimpan: ${res.error || 'unknown'}`);
  };

  const handleReset = () => {
    setDraft((d) => ({ ...d, [activeTpl]: { ...VT_DEFAULTS[activeTpl] } }));
    toast.info(`"${TEMPLATE_LABELS[activeTpl]}" direset ke default (klik Simpan untuk persist)`);
  };

  const handleTestPrint = () => window.print();

  const isSimple = activeTpl === 'simple';

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="voucher-template-editor">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Editor Template Voucher</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Kustom template cetak voucher per-tenant — pilih template lalu edit.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleReset} disabled={saving || loading} data-testid="vt-reset-btn">
            <RotateCcw className="h-4 w-4 mr-2" /> Reset Template Ini
          </Button>
          <Button variant="outline" onClick={handleTestPrint} disabled={saving || loading} data-testid="vt-testprint-btn">
            <Printer className="h-4 w-4 mr-2" /> Test Cetak
          </Button>
          <Button onClick={handleSave} disabled={!isDirty || saving || loading} data-testid="vt-save-btn">
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Menyimpan…' : (isDirty ? 'Simpan Perubahan' : 'Tersimpan')}
          </Button>
        </div>
      </div>

      <Tabs value={activeTpl} onValueChange={setActiveTpl} className="w-full">
        <TabsList className="grid grid-cols-6 max-w-3xl" data-testid="vt-tabs">
          {VT_TEMPLATE_KEYS.map((tk) => (
            <TabsTrigger key={tk} value={tk} data-testid={`vt-tab-${tk}`}>
              {TEMPLATE_LABELS[tk]}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* SETTINGS PANEL */}
        <Card className="lg:col-span-1 no-print">
          <CardHeader>
            <CardTitle className="text-base">Pengaturan — {TEMPLATE_LABELS[activeTpl]}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="vt-prefix">{activeTpl === 'qr' ? 'Teks footer (sebelum nama perusahaan)' : 'Prefix Nomor'}</Label>
              <Input
                id="vt-prefix"
                data-testid="vt-input-prefix"
                value={activeDraft?.prefix_text ?? ''}
                maxLength={20}
                onChange={(e) => patch('prefix_text', e.target.value)}
                placeholder="No."
              />
              <p className="text-xs text-slate-500">
                {isSimple
                  ? 'Contoh: No., Kode, #'
                  : activeTpl === 'compact'
                    ? 'Tampil di pojok kanan atas card. Contoh: No., #, Kode'
                    : activeTpl === 'qr'
                      ? 'Contoh: Distributed by, di-distribusikan oleh, Powered by'
                      : 'Contoh: No.Voucher :, #, Kode :'}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="vt-label">
                {activeTpl === 'compact' ? 'Teks footer (sebelum nama perusahaan)' : 'Label di atas kode voucher'}
              </Label>
              <Input
                id="vt-label"
                data-testid="vt-input-label"
                value={activeDraft?.label_text ?? ''}
                maxLength={40}
                onChange={(e) => patch('label_text', e.target.value)}
                placeholder={activeTpl === 'compact' ? 'di-distribusikan oleh' : 'Voucher Code'}
              />
            </div>

            {activeTpl !== 'compact' && (
              <div className="space-y-2">
                <Label htmlFor="vt-plan">Format teks paket</Label>
                <Input
                  id="vt-plan"
                  data-testid="vt-input-planformat"
                  value={activeDraft?.plan_format ?? ''}
                  maxLength={80}
                  onChange={(e) => patch('plan_format', e.target.value)}
                  placeholder="{duration} - {price}"
                />
                <p className="text-xs text-slate-500">
                  Token: <code>{'{duration}'}</code>, <code>{'{price}'}</code>.
                  Contoh: <code>{'{price}'} / {'{duration}'}</code>
                </p>
              </div>
            )}

            {isSimple && (
              <div className="space-y-2">
                <Label htmlFor="vt-brandpos">Posisi Nama PT</Label>
                <Select
                  value={activeDraft?.brand_position ?? 'footer'}
                  onValueChange={(v) => patch('brand_position', v)}
                >
                  <SelectTrigger id="vt-brandpos" data-testid="vt-select-brandpos">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="top">Di atas (top)</SelectItem>
                    <SelectItem value="footer">Di bawah (footer)</SelectItem>
                    <SelectItem value="hide">Sembunyikan</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {['font_brand', 'font_plan', 'font_label', 'font_code'].map((k) => {
              const labels = {
                font_brand: (activeTpl === 'compact' || activeTpl === 'qr') ? 'Font footer (nama perusahaan)' : 'Font nama PT / brand',
                font_plan: activeTpl === 'compact' ? 'Font isi (durasi & kode)' : activeTpl === 'qr' ? 'Font banner harga' : 'Font durasi + harga',
                font_label: 'Font label',
                font_code: activeTpl === 'compact' ? '(sama dengan font isi)' : 'Font kode voucher',
              };
              // For compact: font_code is same as font_plan (both use compact-value), so hide font_code
              if (activeTpl === 'compact' && k === 'font_code') return null;
              return (
                <div key={k} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor={`vt-${k}`}>{labels[k]}</Label>
                    <span className="text-xs font-mono text-slate-600">{Number(activeDraft?.[k] ?? 0).toFixed(1)} pt</span>
                  </div>
                  <Slider
                    id={`vt-${k}`}
                    data-testid={`vt-slider-${k}`}
                    min={4}
                    max={30}
                    step={0.5}
                    value={[Number(activeDraft?.[k] ?? 8)]}
                    onValueChange={(v) => patch(k, fontClamp(v?.[0]))}
                  />
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* LIVE PREVIEW */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">Live Preview — {TEMPLATE_LABELS[activeTpl]}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="border rounded-md bg-slate-50 p-2 overflow-auto" style={{ maxHeight: '75vh' }} data-testid="vt-preview-wrapper">
              <div style={{ transform: 'scale(0.55)', transformOrigin: 'top left', width: '182%' }}>
                {renderPreview(activeTpl, {
                  vouchers: PREVIEW_VOUCHERS,
                  companyName: 'PT. PUSAKA KREASI MANDIRI',
                  templateSettings: activeDraft,
                  batchHeader: <BatchHeader pkgName='HOTSPOT 12 JAM MAYAK' priceStr='Rp. 3.000' createdAt='20-07-2026 12:00' count={100} />,
                })}
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-2">Preview di-scale 55%. Cetak asli tetap ukuran A4 penuh.</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default VoucherTemplateEditor;
